/* Firestore collection names + small id helper. Keep all names in one place. */
export const COL = {
  users: 'users',
  usernameLookup: 'usernameLookup',
  players: 'players',
  teams: 'teams',
  teamNameLookup: 'teamNameLookup', // platform-wide unique team-name claims
  playerNameLookup: 'playerNameLookup', // platform-wide unique player-name claims
  clubs: 'clubs',
  seasons: 'seasons',
  tournaments: 'tournaments',
  matches: 'matches',
  deliveries: 'deliveries', // subcollection under matches/{id}
  playerStats: 'playerStats',
  teamStats: 'teamStats',
  standings: 'standings', // subcollection under tournaments/{id}
  activity: 'activity',
  settings: 'settings',
  adminRequests: 'adminRequests',
  auditLogs: 'auditLogs',
  userPrefs: 'userPrefs', // per-user appearance/accessibility prefs (cross-device)
  recoveryAttempts: 'recoveryAttempts', // audit trail for /recover quiz attempts
  ballMeta: 'ballMeta', // subcollection under matches/{id}; optional shot-zone/line-length tags
  notifications: 'notifications', // per-user notification center entries
  clientErrors: 'clientErrors', // runtime errors caught by ErrorBoundary, for admin diagnostics
  entityVersions: 'entityVersions', // pre-edit snapshots of players/teams/clubs/tournaments/matches
  featureFlags: 'featureFlags',
  invitations: 'invitations',
  // Internal linkage doc consumed only by firestore.rules (users/{uid}'s self-role-elevation
  // check on invitation accept) — never read directly by any UI. See RESTRICTIONS.md for why
  // this exists: Firestore rules can't query "is there a pending invitation for this uid" by
  // field value, only by exact doc path, so this mirrors {role, expiresAt} at invitedUid.
  invitationRoleGrants: 'invitationRoleGrants',
  teamInvitations: 'teamInvitations', // invites an existing user to join a team's roster as a player
  // Mirrors invitationRoleGrants' role for team invitations: firestore.rules can't query "is
  // there a pending team invitation for this uid," only check an exact doc path, so this
  // records {teamId, expiresAt} at invitedUid — the narrow, unforgeable exception that lets the
  // invitee create/update their own linked Player doc and append to the team's playerIds.
  teamInvitationGrants: 'teamInvitationGrants',
  comments: 'comments', // flat spectator comments, scoped to a matchId
  reactions: 'reactions', // subcollection under matches/{id}; one doc per reacting user
  announcements: 'announcements', // scoped by tournamentId
  // Doc id == uid, one per user (mirrors userPrefs). `provider` is always 'mock' today — this
  // project has no real billing integration yet, see billing.types.ts.
  subscriptions: 'subscriptions',
  // Media/broadcast engine (live streaming, recording, replay, clips) — see types/index.ts's
  // "Media/Broadcast engine" section for the full architecture writeup.
  broadcasts: 'broadcasts', // doc id == matchId
  broadcastViewers: 'viewers', // subcollection under broadcasts/{matchId}
  broadcasterCandidates: 'broadcasterCandidates', // subcollection under .../viewers/{connId}
  viewerCandidates: 'viewerCandidates', // subcollection under .../viewers/{connId}
  matchVideos: 'matchVideos',
  clips: 'clips',
  // Discovery + Looking For + Community engine.
  lookingForPosts: 'lookingForPosts',
  lookingForResponses: 'lookingForResponses',
  follows: 'follows',
  communityPosts: 'communityPosts',
  postLikes: 'postLikes',
  contentReports: 'contentReports',
  // Identity/Reputation engine.
  ratings: 'ratings',
  // Platform-wide release/update banners (new version, security, maintenance heads-up) — see
  // PlatformAnnouncement in types/index.ts. Distinct from `announcements` (tournament-scoped).
  platformAnnouncements: 'platformAnnouncements',
  // Doc id == `${blockerUid}_${blockedUid}` — idempotent create, and lets firestore.rules check
  // "did X block Y" by exact path (`exists()`) rather than a query, same trick as `follows`.
  // Scoped to the blocker's own content (a team/club owner blocking a requester, a Looking For
  // poster blocking a responder) — not a platform-wide block.
  userBlocks: 'userBlocks',
  // Team/club join requests. Doc id == `${targetType}_${targetId}_${requesterId}` — deterministic
  // so a duplicate *pending* request is a rules-level rejection, not a second document.
  joinRequests: 'joinRequests',
  // Doc id == uid. Rolling-window counter backing joinRequests' server-side rate limit — see
  // firestore.rules' matching block for why this is the real enforcement, not just this client.
  joinRequestLimits: 'joinRequestLimits',
  bugReports: 'bugReports',
  improvementRequests: 'improvementRequests',
  // Doc id == uid. Rolling-window counter backing improvementRequests' server-side rate limit —
  // same pattern as joinRequestLimits, deliberately a separate counter so maxing out one action
  // never blocks the other.
  improvementLimits: 'improvementLimits',
  // Doc id == `${sortedUidA}_${sortedUidB}` — deterministic so there is structurally exactly one
  // conversation per pair of users, same trick as `follows`/`ratings`/`joinRequests`.
  conversations: 'conversations',
  // Subcollection under conversations/{id}. Immutable once sent.
  messages: 'messages',
  // Doc id == uid. Rolling-window counter backing direct-message send rate limiting.
  messageLimits: 'messageLimits',
  conversationLimits: 'conversationLimits',
  // Camera Management engine — private per-account (unlike most cricket data, NOT public-read).
  // See types/index.ts's "Camera Management engine" section for the full architecture writeup.
  cameras: 'cameras',
  cameraStations: 'cameraStations',
  cameraStationPairings: 'cameraStationPairings',
  // Phone-camera extension — see types/index.ts's CameraPairing/CameraStream doc comments.
  cameraPairings: 'cameraPairings',
  cameraStreams: 'cameraStreams', // doc id == cameraId
  cameraStreamViewers: 'viewers', // subcollection under cameraStreams/{cameraId}
  cameraStreamBroadcasterCandidates: 'broadcasterCandidates', // subcollection under .../viewers/{connId}
  cameraStreamViewerCandidates: 'viewerCandidates', // subcollection under .../viewers/{connId}
  // OBS output extension — see types/index.ts's ProgramStream doc comment. Same subcollection
  // shape as cameraStreams, one level up (keyed by stationId, not cameraId).
  programStreams: 'programStreams', // doc id == stationId
  programStreamViewers: 'viewers',
  programStreamBroadcasterCandidates: 'broadcasterCandidates',
  programStreamViewerCandidates: 'viewerCandidates',
  // Which distinct Camera Stations are assigned to a match — the Free-plan station-count limit's
  // single source of truth. See types/index.ts's MatchCameraStations doc comment.
  matchCameraStations: 'matchCameraStations', // doc id == matchId
} as const

export const SETTINGS_DOC = 'app'

/** Loose unique-ish id for client-generated docs (deliveries etc.). */
export function genId(prefix = ''): string {
  return `${prefix}${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 8)}`
}

/**
 * Firestore rejects `undefined` field values. Strip them before any write so
 * optional/blank form fields don't blow up setDoc/updateDoc. Use `null` if you
 * actually need to clear a field.
 */
export function pruneUndefined<T extends object>(obj: T): T {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) out[k] = v
  }
  return out as T
}
